import * as types from './types'
import {
  GETSupportWholesalerResponse,
  IPaginatedRequest,
  IPostWholesalerCatalog,
  IResponse,
  IToggleOrderSystemRequest,
  IWholesalerCatalog,
  IWholesalerOrderSystem,
  IWholesalerService,
  POSTSupportWholesalerBody,
  POSTSupportWholesalerResponse,
  PUTSupportWholesalerBody
} from '@cbgms/api'
import { CountryCode } from '@carsys/enums/country-code'
import { OrderSystem } from '@cbgms/api/modules/enums'
import { AsyncAction } from '@cbgms/base/redux/async-action'
import { DashboardCatalogInfo } from '../components/catalog/types'

export const getWholesalerList = (params: IPaginatedRequest) => ({
  type: [types.LOAD_WHOLESALERS, types.LOAD_WHOLESALERS_SUCCESS, types.LOAD_WHOLESALERS_FAILED],
  payload: {
    api: 'support',
    request: {
      method: 'GET',
      url: '/modules/supported-wholesaler',
      params
    }
  }
})

export const createWholesaler = (values: POSTSupportWholesalerBody) =>
  AsyncAction<POSTSupportWholesalerResponse>({
    type: [types.CREATE_WHOLESALER, types.CREATE_WHOLESALER_SUCCESS, types.CREATE_WHOLESALER_FAILED],
    payload: {
      api: 'support',
      request: {
        method: 'POST',
        url: '/modules/supported-wholesaler',
        data: values
      }
    }
  })

export const updateWholesaler = (uuid: string, values: PUTSupportWholesalerBody) => ({
  type: [types.CREATE_WHOLESALER, types.CREATE_WHOLESALER_SUCCESS, types.CREATE_WHOLESALER_FAILED],
  payload: {
    api: 'support',
    request: {
      method: 'PUT',
      url: `/modules/supported-wholesaler/${uuid}`,
      data: values
    }
  }
})

export const loadWholesaler = (uuid: string) =>
  AsyncAction<GETSupportWholesalerResponse>({
    type: [types.LOAD_WHOLESALER, types.LOAD_WHOLESALER_SUCCESS, types.LOAD_WHOLESALER_FAILED],
    payload: {
      api: 'support',
      request: {
        method: 'GET',
        url: `/modules/supported-wholesaler/${uuid}`
      }
    }
  })

export const loadWholesalerOrdersystems = (countryCode: string) =>
  AsyncAction<IResponse<IWholesalerOrderSystem[]>>({
    type: [types.LOAD_WHOLESALER_ORDERSYSTEMS, types.LOAD_WHOLESALER_ORDERSYSTEMS_SUCCESS, types.LOAD_WHOLESALER_ORDERSYSTEMS_FAILED],
    payload: {
      api: 'support',
      request: {
        method: 'GET',
        url: `/modules/supported-wholesaler/order-systems-by-country/${countryCode}`
      }
    }
  })

export const loadWholesalerServices = (uuid: string, countryCode: CountryCode, orderSystem: OrderSystem) =>
  AsyncAction<IResponse<IWholesalerService[]>>({
    type: [types.LOAD_WHOLESALER_SERVICES, types.LOAD_WHOLESALER_SERVICES_SUCCESS, types.LOAD_WHOLESALER_SERVICES_FAILED],
    payload: {
      api: 'support',
      request: {
        method: 'GET',
        url: `/modules/supported-wholesaler/${uuid}/order-system-active-services`,
        params: {
          CountryCode: countryCode,
          OrderSystem: orderSystem
        }
      }
    }
  })

export const toggleWholesalerService = (uuid: string, values: IToggleOrderSystemRequest) => ({
  type: [types.TOGGLE_WHOLESALER_SERVICE, types.TOGGLE_WHOLESALER_SERVICE_SUCCESS, types.TOGGLE_WHOLESALER_SERVICE_FAILED],
  payload: {
    api: 'support',
    request: {
      method: 'PUT',
      url: `/modules/supported-wholesaler/${uuid}/toggle-order-system`,
      data: values
    }
  }
})

export const clearWholesaler = () => ({
  type: types.CLEAR_WHOLESALER
})

export const loadWholesalerCatalogs = (wholesaleUUID: string) =>
  AsyncAction<IResponse<IWholesalerCatalog[]>>({
    type: [types.GET_WHOLESALER_CATALOGS, types.GET_WHOLESALER_CATALOGS_SUCCESS, types.GET_WHOLESALER_CATALOGS_FAILED],
    payload: {
      api: 'support',
      request: {
        method: 'GET',
        url: `/modules/supported-wholesaler/${wholesaleUUID}/catalogs`
      }
    }
  })

export const loadExternalResources = () =>
  AsyncAction<IResponse<DashboardCatalogInfo>>({
    type: [types.GET_EXTERNAL_RESOURCES, types.GET_EXTERNAL_RESOURCES_SUCCESS, types.GET_EXTERNAL_RESOURCES_FAILED],
    payload: {
      api: 'support',
      request: {
        method: 'GET',
        url: `/modules/external-resources`
      }
    }
  })

export const deleteWholesalerCatalog = (wholesaleUUID: string, catalogUUID: string) =>
  AsyncAction<IResponse<IWholesalerCatalog[]>>({
    type: [types.DELETE_WHOLESALER_CATALOGS, types.DELETE_WHOLESALER_CATALOGS_SUCCESS, types.DELETE_WHOLESALER_CATALOGS_FAILED],
    payload: {
      api: 'support',
      request: {
        method: 'DELETE',
        url: `/modules/supported-wholesaler/${wholesaleUUID}/catalogs/${catalogUUID}`,
        data: {
          DryRun: false
        }
      }
    }
  })

export const createWholesalerCatalog = (wholesaleUUID: string, data: IPostWholesalerCatalog) =>
  AsyncAction<IResponse<IWholesalerCatalog[]>>({
    type: [types.CREATE_WHOLESALER_CATALOGS, types.CREATE_WHOLESALER_CATALOGS_SUCCESS, types.CREATE_WHOLESALER_CATALOGS_FAILED],
    payload: {
      api: 'support',
      request: {
        method: 'POST',
        url: `/modules/supported-wholesaler/${wholesaleUUID}/catalogs`,
        data
      }
    }
  })

export const editWholesalerCatalog = (wholesaleUUID: string, catalogUUID: string, data: IPostWholesalerCatalog) =>
  AsyncAction<IResponse<IWholesalerCatalog[]>>({
    type: [types.CREATE_WHOLESALER_CATALOGS, types.CREATE_WHOLESALER_CATALOGS_SUCCESS, types.CREATE_WHOLESALER_CATALOGS_FAILED],
    payload: {
      api: 'support',
      request: {
        method: 'PUT',
        url: `/modules/supported-wholesaler/${wholesaleUUID}/catalogs/${catalogUUID}`,
        data
      }
    }
  })

export type UploadJSONFormResponse = IResponse<{
  Success: boolean
  Message: string
}>

export const uploadJSONForm = (wholesalerUUID: string, file: File, formName: string) => {
  const formData = new FormData()
  formData.append('file', file)

  return AsyncAction<UploadJSONFormResponse>({
    type: [types.UPLOAD_JSON_FORM, types.UPLOAD_JSON_FORM_SUCCESS, types.UPLOAD_JSON_FORM_FAILED],
    payload: {
      api: 'support',
      request: {
        method: 'POST',
        url: `/modules/forms/${wholesalerUUID}/upload`,
        params: {
          name: formName
        },
        data: formData
      }
    }
  })
}

export const downloadJSONForm = (wholesalerUUID: string, formName: string) => {
  return AsyncAction<Blob>({
    type: [types.DOWNLOAD_JSON_FORM, types.DOWNLOAD_JSON_FORM_SUCCESS, types.DOWNLOAD_JSON_FORM_FAILED],
    payload: {
      api: 'support',
      request: {
        method: 'GET',
        url: `/modules/forms/${wholesalerUUID}/download/${formName}`,
        responseType: 'blob'
      }
    }
  })
}

export const downloadJSONFormTemplate = (wholesalerUUID: string, formName: string) => {
  return AsyncAction<Blob>({
    type: [types.DOWNLOAD_JSON_FORM_TEMPLATE, types.DOWNLOAD_JSON_FORM_TEMPLATE_SUCCESS, types.DOWNLOAD_JSON_FORM_TEMPLATE_FAILED],
    payload: {
      api: 'support',
      request: {
        method: 'GET',
        url: `/modules/forms/${wholesalerUUID}/download/${formName}`,
        params: {
          TemplateDownload: true
        },
        responseType: 'blob'
      }
    }
  })
}
